import { useTranslation } from 'react-i18next';
import {
	DetailField,
	DetailFieldsGrid,
	DetailSection
} from '@/components/ui/details-panel';
import { Badge } from '@/components/ui/badge';
import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

interface AuditData {
	createdAt?: string | null;
	updatedAt?: string | null;
	activatedAt?: string | null;
	deactivatedAt?: string | null;
	deletedAt?: string | null;
	createdBy?: string | null;
	updatedBy?: string | null;
}

interface AuditHistoryItem {
	id: string;
	userId: string;
	userName: string;
	timestamp: string;
	changeType: 'create' | 'update' | 'delete' | 'activate' | 'deactivate';
	changes?: Record<string, { old: unknown; new: unknown }>;
}

interface AuditTabProps {
	data: AuditData;
	history?: AuditHistoryItem[];
}

const formatDate = (value?: string | null) => {
	if (!value) {
		return null;
	}

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return value;
	}

	return date.toLocaleString();
};

const HistoryItemDetails = ({ item }: { item: AuditHistoryItem }) => {
	const { t } = useTranslation('common');
	const [isExpanded, setIsExpanded] = useState(false);

	const getChangeTypeBadge = (type: AuditHistoryItem['changeType']) => {
		const variants: Record<
			AuditHistoryItem['changeType'],
			'default' | 'secondary' | 'destructive' | 'outline'
		> = {
			create: 'default',
			update: 'secondary',
			delete: 'destructive',
			activate: 'default',
			deactivate: 'outline'
		};

		return (
			<Badge variant={variants[type]}>
				{t(`audit.changeTypes.${type}`)}
			</Badge>
		);
	};

	return (
		<div className="p-4 border border-gray-200 rounded-lg bg-gray-50/50 dark:bg-gray-800/50 dark:border-gray-700">
			<button
				onClick={() => setIsExpanded(!isExpanded)}
				className="flex items-center justify-between w-full gap-2 text-left"
			>
				<div className="flex items-center gap-3">
					{isExpanded ? (
						<ChevronDown className="w-4 h-4 text-gray-500" />
					) : (
						<ChevronRight className="w-4 h-4 text-gray-500" />
					)}
					<div className="flex flex-col gap-1">
						<div className="flex items-center gap-2">
							<span className="text-sm font-semibold text-gray-900 dark:text-gray-50">
								{item.userName}
							</span>
							{getChangeTypeBadge(item.changeType)}
						</div>
						<span className="text-xs text-gray-500 dark:text-slate-400">
							{formatDate(item.timestamp)}
						</span>
					</div>
				</div>
			</button>

			{isExpanded && item.changes && (
				<div className="pt-4 mt-4 space-y-2 border-t border-gray-200 dark:border-gray-700">
					{Object.entries(item.changes).map(([field, change]) => (
						<div
							key={field}
							className="text-sm"
						>
							<span className="font-medium text-gray-700 dark:text-gray-300">
								{field}:
							</span>
							<div className="flex gap-2 mt-1 ml-4">
								<span className="text-gray-500 line-through dark:text-slate-400">
									{String(change.old)}
								</span>
								<span className="text-gray-500 dark:text-slate-400">
									→
								</span>
								<span className="font-medium text-gray-900 dark:text-gray-50">
									{String(change.new)}
								</span>
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	);
};

export const AuditTab = ({ data, history = [] }: AuditTabProps) => {
	const { t } = useTranslation('common');

	// Generate dummy history if none provided
	const dummyHistory: AuditHistoryItem[] =
		history.length > 0
			? history
			: [
					{
						id: '1',
						userId: 'user-123',
						userName: 'John Doe',
						timestamp: data.createdAt || new Date().toISOString(),
						changeType: 'create',
						changes: {}
					}
				];

	return (
		<div className="space-y-6">
			{/* Audit Information Section */}
			<DetailSection title={t('audit.information')} noBorderTop>
				<DetailFieldsGrid>
					{data.createdAt && (
						<DetailField
							label={t('audit.created')}
							value={formatDate(data.createdAt)}
						/>
					)}
					{data.createdBy && (
						<DetailField
							label={t('audit.createdBy')}
							value={data.createdBy}
						/>
					)}
					{data.updatedAt && (
						<DetailField
							label={t('audit.updated')}
							value={formatDate(data.updatedAt)}
						/>
					)}
					{data.updatedBy && (
						<DetailField
							label={t('audit.updatedBy')}
							value={data.updatedBy}
						/>
					)}
					{data.activatedAt && (
						<DetailField
							label={t('audit.activated')}
							value={formatDate(data.activatedAt)}
						/>
					)}
					{data.deactivatedAt && (
						<DetailField
							label={t('audit.deactivated')}
							value={formatDate(data.deactivatedAt)}
						/>
					)}
					{data.deletedAt && (
						<DetailField
							label={t('audit.deleted')}
							value={formatDate(data.deletedAt)}
						/>
					)}
				</DetailFieldsGrid>
			</DetailSection>

			{/* Change History Section */}
			<DetailSection title={t('audit.history')}>
				<div className="space-y-3">
					{dummyHistory.map(item => (
						<HistoryItemDetails
							key={item.id}
							item={item}
						/>
					))}
				</div>
			</DetailSection>
		</div>
	);
};
